import { useFrame, useThree } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { Vector3, type Group } from 'three';
import type { RegionKind, RegionTree } from '@map/shared';
import type { Territory } from '@map/worldkit';
import { visibleTerritories } from './visible.js';

export interface PlacedLabel {
  id: string;
  name: string;
  kind: RegionKind;
  selected: boolean;
  x: number;
  y: number;
  opacity: number;
}

/**
 * MAP-089. Works out where territory names should sit, in screen space.
 *
 * Three rules keep them readable rather than a thicket: anything on the far
 * side of the globe is hidden, because a label floating over the back of a
 * sphere reads as being somewhere it is not; where two would overlap the larger
 * territory keeps its name; and a territory too small on screen to hold its
 * name does not get one.
 *
 * The placement runs in the scene, where the camera is, but the text is drawn
 * in the DOM overlay above it, where it stays crisp at any zoom.
 */
export function LabelProjector({
  territories,
  tree,
  depth,
  selectedId,
  onPlace,
}: {
  territories: Map<string, Territory>;
  tree: RegionTree;
  depth: number;
  selectedId: string | null;
  onPlace: (labels: PlacedLabel[]) => void;
}) {
  const { camera, size } = useThree();
  const scratch = useRef(new Vector3());
  const previous = useRef<PlacedLabel[]>([]);
  // Territory sites are in the globe's own coordinates, and the globe turns.
  // Without this anchor to read the rotation from, every name was projected at
  // the position the territory held before the world span — which put most of
  // them behind the sphere, and the far-side test then threw them all away.
  const anchor = useRef<Group>(null);

  const candidates = useMemo(() => {
    // Exactly what the globe is drawing, so a name can never belong to a place
    // that is not on the screen.
    const out = visibleTerritories(territories, tree, depth, selectedId).map(
      ({ region, territory }) => ({
        id: region.id,
        name: region.name,
        kind: region.kind,
        site: territory.site,
        area: territory.area,
      }),
    );

    // The opened place keeps its own name even while its children are shown
    // inside it — that name is where you are.
    if (selectedId && !out.some((c) => c.id === selectedId)) {
      const region = tree.regions[selectedId];
      const territory = territories.get(selectedId);
      if (region && territory) {
        out.push({
          id: region.id,
          name: region.name,
          kind: region.kind,
          site: territory.site,
          area: territory.area,
        });
      }
    }

    // Largest first, so the big territory wins any collision — except the
    // selected one, which is what you are looking at and always gets its name.
    out.sort((a, b) => b.area - a.area);
    const chosen = out.findIndex((c) => c.id === selectedId);
    if (chosen > 0) out.unshift(...out.splice(chosen, 1));
    // Cities are always named, so they claim their room before anything else;
    // a country opened around them moves its own name out of their way.
    return [...out.filter((c) => isSettlement(c.kind)), ...out.filter((c) => !isSettlement(c.kind))];
  }, [territories, tree, depth, selectedId]);

  useFrame(() => {
    const next: PlacedLabel[] = [];
    const taken: Array<{ x: number; y: number; w: number; h: number }> = [];
    const facing = camera.position.clone().normalize();

    for (const candidate of candidates) {
      const world = scratch.current.set(
        candidate.site.x * 1.02,
        candidate.site.y * 1.02,
        candidate.site.z * 1.02,
      );
      if (anchor.current) world.applyMatrix4(anchor.current.matrixWorld);

      if (world.clone().normalize().dot(facing) < 0.15) continue;

      const projected = world.clone().project(camera);
      const x = (projected.x * 0.5 + 0.5) * size.width;
      const y = (-projected.y * 0.5 + 0.5) * size.height;
      if (x < 8 || y < 8 || x > size.width - 8 || y > size.height - 8) continue;

      // Roughly how wide the territory is on screen, so a name is not printed
      // across something far too small to carry it.
      const apparent = Math.sqrt(candidate.area) * size.height * (1.8 / camera.position.length());
      // Capitals and letter spacing make the larger names wider per letter.
      const perLetter = candidate.kind === 'continent' ? 11.5 : candidate.kind === 'country' ? 9.5 : 7;
      const width = candidate.name.length * perLetter + 10;
      // The selected territory keeps its name however small it is on screen:
      // an unlabelled place you have just clicked into is the one case where
      // you genuinely do not know what you are looking at.
      const settlement = isSettlement(candidate.kind);
      if (!settlement && candidate.id !== selectedId && apparent < width * 0.4) continue;

      const overlapping = (box: { x: number; y: number; w: number; h: number }) =>
        taken.some(
          (other) =>
            box.x < other.x + other.w &&
            box.x + box.w > other.x &&
            box.y < other.y + other.h &&
            box.y + box.h > other.y,
        );

      // A larger name that lands on a city steps up above it rather than
      // sharing its spot; one that still finds no room is dropped, unless it
      // is where you are.
      let lift = 0;
      let box = { x: x - width / 2, y: y - 9, w: width, h: 18 };
      if (!settlement) {
        while (overlapping(box) && lift < 4) {
          lift += 1;
          box = { ...box, y: box.y - 18 };
        }
        if (overlapping(box) && candidate.id !== selectedId) continue;
      }

      taken.push(box);
      next.push({
        id: candidate.id,
        name: candidate.name,
        kind: candidate.kind,
        selected: candidate.id === selectedId,
        x,
        y: y - lift * 18,
        opacity: candidate.id === selectedId ? 1 : 0.78,
      });
    }

    const changed =
      previous.current.length !== next.length ||
      next.some((label, i) => {
        const before = previous.current[i];
        return (
          !before ||
          before.id !== label.id ||
          Math.abs(before.x - label.x) > 0.5 ||
          Math.abs(before.y - label.y) > 0.5
        );
      });

    if (changed) {
      previous.current = next;
      onPlace(next);
    }
  });

  return <group ref={anchor} />;
}

/** Cities are always named; the plots inside one are named only where there is room. */
function isSettlement(kind: RegionKind): boolean {
  return kind === 'city';
}

/**
 * Map typography, doing the job it does on a paper map: a continent is not
 * written the same way as a city, so the size of a place is legible from its
 * name alone.
 */
const STYLES: Record<RegionKind, { size: string; spacing: string; caps: boolean; weight: number }> = {
  continent: { size: '0.9375rem', spacing: '0.18em', caps: true, weight: 500 },
  country: { size: '0.8125rem', spacing: '0.1em', caps: true, weight: 500 },
  city: { size: '0.6875rem', spacing: '0.02em', caps: false, weight: 400 },
  district: { size: '0.625rem', spacing: '0.02em', caps: false, weight: 400 },
};

export function LabelOverlay({ labels }: { labels: PlacedLabel[] }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {labels.map((label) => {
        const style = STYLES[label.kind];
        return (
          <span
            key={label.id}
            className="absolute flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 whitespace-nowrap"
            style={{
              left: label.x,
              top: label.y,
              opacity: label.opacity,
              color: 'var(--text)',
              textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.6)',
              fontSize: style.size,
              fontWeight: label.selected ? 600 : style.weight,
              letterSpacing: style.spacing,
              textTransform: style.caps ? 'uppercase' : 'none',
            }}
          >
            {/* Settlements get a dot, the way they do on a map. */}
            {(label.kind === 'city' || label.kind === 'district') && (
              <span
                className="inline-block h-1 w-1 rounded-full"
                style={{ background: 'var(--text)', opacity: 0.8 }}
              />
            )}
            {label.name}
          </span>
        );
      })}
    </div>
  );
}
